import { store } from '../../../redux/store';
import {
  createNewChat,
  selectChat,
  setChat,
  setChats,
  setCurrentThreadId,
  setErrorMessages,
  setLoadingMessages,
  setLoadingMessagesEnd,
  setMessageIsFail,
  setMessageIsLoading,
  setMessages,
  setMetadata,
  clearMetadata,
  setMessageSuccess,
  setReferences,
  setStreamMessageLoadingId,
  setTypingStatus,
  setShowDebug,
} from './reducer';
import {
  ChatItemType,
  LinksMetaData,
  MessageItem,
  SendMessageWithImageParams,
} from '../../../interfaces/reducers/chat';
import {
  finishLoading,
  startLoading,
} from '../../../connected-components/loading/actions';
import { ChatType, MessageStatus, TabIds, ToastType } from '../../../utils/enums';
import { services } from '../../../api';
import { QNAForm } from '../../../interfaces/api/qna';
import { throttle } from 'lodash';
import dayjs from 'dayjs';
import { cleanAndFormatMarkdown } from '../../../utils/markdownHelpers';
import { tr } from '../../../translations';
import { getLocalData, setLocalData } from '../../../utils/storage';
import { keys } from '../../../api/keys';
import { endSession } from '../../auth/login/actions';
import { baseUrl, api } from '../../../api/api';
import Config from 'react-native-config';
import { showGlobalToast } from '../../../connected-components/toast/actions';
import { CategoryItem } from '../../../interfaces/components/chatList';
import { Clipboard } from 'react-native';
import { ChatItemResponse } from '../../../api/content/chats';
import { stopStreamingSSE } from '../../../api/content/qna';
import parseMetadata from '../../../utils/metadata';

const IS_CHAT_DEBUGGING = false;

// Global declaration for stream controller
declare global {
  interface Window {
    streamController?: AbortController;
  }
}

// Local implementation of missing functions
const generateUniqueId = (prefix = '') => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${prefix}_${timestamp}_${random}`;
};

const encryptUrlParam = (param: string) => {
  // Simple base64 encoding for now
  return btoa(param);
};

/**
 * Fetches available chat categories from the API
 *
 * @returns {Promise<CategoryItem[] | null>} Promise resolving to categories list
 */
export const getCategories = async (
  tabId?: string,
): Promise<CategoryItem[] | null> => {
  try {
    const res = await services.qna.getCategories(tabId || '2');
    if (!res) {
      throw new Error('Error Fetching Categories');
    }
    if (Array.isArray(res.content)) {
      return res.content;
    }
    return null;
  } catch (error: any) {
    return null;
  }
};

/**
 * Fetches chat history with optional filtering parameters
 *
 * @param {number} [start=0] - Starting index for pagination
 * @param {number} [length=50] - Number of chats to fetch
 * @param {string} [search=''] - Search term for filtering chats
 * @param {number} [favourite=0] - Filter by favorite status (1 for favorites only)
 * @param {string} [tab_id] - Filter by tab ID
 * @returns {Promise<{ success: boolean; chats: ChatItemType[] }>} Object containing success status and chats array
 */
export const getChatHistory = async (
  start: number = 0,
  length: number = 50,
  search: string = '',
  favourite: number = 0,
  tab_id?: string,
): Promise<{ success: boolean; chats: ChatItemResponse[] }> => {
  try {
    const response = await services.chats.getChats({
      start,
      length,
      search,
      favourite,
      tab_id,
      type:
        tab_id && +tab_id === TabIds.QA ? ChatType.QNA : ChatType.ClinicalCase,
    });

    return {
      success: true,
      chats: response?.content || [],
    };
  } catch (error) {
    return {
      success: false,
      chats: [],
    };
  }
};

/**
 * Fetches favorite chats using the new /favorite endpoint
 *
 * @param {number} [page=1] - Page number for pagination
 * @param {number} [pageSize=10] - Number of favorites per page
 * @param {ChatType} [thread_type] - Thread type to filter favorites by
 * @param {string} [tab_id] - Tab ID to filter favorites by
 * @returns {Promise<{ success: boolean; chats: ChatItemResponse[]; total: number; hasMore: boolean }>} Object containing success status, favorites array, total count, and pagination info
 */
export const getFavoriteChats = async (
  page: number = 1,
  pageSize: number = 10,
  thread_type?: ChatType,
  tab_id?: string,
): Promise<{
  success: boolean;
  chats: ChatItemResponse[];
  total: number;
  hasMore: boolean;
}> => {
  try {
    const response = await services.chats.getFavorites({
      page,
      pageSize,
      type: Config.SHARE_FAVORITE_TYPE || 'Chat',
      thread_type,
      tab_id,
    });

    const content = response?.content;
    const chats = content?.data || [];
    const total = content?.total || 0;
    const hasMore = page * pageSize < total;

    return {
      success: true,
      chats,
      total,
      hasMore,
    };
  } catch (error) {
    return {
      success: false,
      chats: [],
      total: 0,
      hasMore: false,
    };
  }
};

/**
 * Fetches developer messages for a specific chat
 *
 * @param {number|string} id - Chat ID to fetch developer messages for
 * @returns {Promise<any[]>} Array of developer messages
 */
export const getDevMessages = async (id: number | string): Promise<any[]> => {
  try {
    const res = await services.qna.getDevMessages(id);
    if (res.content) {
      return res.content;
    }
    return [];
  } catch (ex) {
    return [];
  }
};

/**
 * Fetches message history for a specific chat
 *
 * @param {number} chatId - ID of the chat to fetch messages for
 * @param {number} [start=0] - Starting index for pagination
 * @param {number} [length=50] - Number of messages to fetch
 * @param {string} [search=''] - Search term for filtering messages
 * @returns {Promise<{ messages: MessageItem[]; metadata: LinksMetaData }>} Object containing messages and metadata
 */
export const getMessagesHistory = async (
  chatId: number,
  start: number = 0,
  length: number = 50,
  search: string = '',
): Promise<{ messages: MessageItem[]; metadata: LinksMetaData }> => {
  try {
    const params = { id: chatId, start, length, search };
    const res = await services.qna.getChatMessages(params);
    if (res.content) {
      stopStreamingSSE();

      const apiMessages = (res?.content?.messages || []) as any[];
      if (res?.content?.id) {
        store.dispatch(setCurrentThreadId(res?.content?.id));
      }
      // Normalize to UI MessageItem shape and sort newest-first
      const normalized: MessageItem[] = apiMessages
        .map(msg => ({
          id:
            (msg.id ?? msg.message_id ?? msg.uuid)?.toString?.() || `${msg.id}`,
          content: msg.content ?? msg.message ?? '',
          message: msg.content ?? msg.message ?? '', // Keep for backward compatibility
          role: msg.role,
          created_at: msg.created_at,
          updated_at: msg.updated_at,
          thread_id: msg.thread_id?.toString?.(),
          sender_id: (msg.user_id ?? msg.sender_id)?.toString?.(),
          images: msg.images,
          class_name: msg.class_name,
          sender_name: msg.sender_name,
          references: msg.references || msg.citations,
          citations: msg.citations,
          dev_message_id: msg.dev_message_id,
          category_key: msg.category_key,
          category_name: msg.category_name,
          category_id: msg.category_id,
        }))
        .filter(m => typeof (m.content || m.message) === 'string');

      normalized.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      const showDebug = !!(res?.content as any)?.show_debug;
      if (showDebug || res?.content?.dev_message_id) {
        store.dispatch(setShowDebug(true));
        const devMessageId = (res?.content as any)?.dev_message_id;
        if (devMessageId) {
          const firstAssistant = normalized.find(m => m.role === 'assistant');
          if (firstAssistant) {
            firstAssistant.dev_message_id = devMessageId;
          }
        }
      } else {
        store.dispatch(setShowDebug(false));
      }
      store.dispatch(setChat({ data: normalized }));
      const linksMeta =
        (res.content as any)?.links_meta ||
        (res.content as any)?.links_metadata;
      if (linksMeta) {
        store.dispatch(setMetadata(linksMeta));
      }
      const payload = {
        messages: normalized,
        metadata: linksMeta,
      } as { messages: MessageItem[]; metadata: LinksMetaData };
      return payload;
    }
    return {
      messages: [],
      metadata: {} as LinksMetaData,
    };
  } catch (ex) {
    store.dispatch(setErrorMessages());
    return {
      messages: [],
      metadata: {} as LinksMetaData,
    };
  }
};

/**
 * Shares a chat with others by creating a public link
 *
 * @param {string} id - ID of the chat to share
 * @returns {Promise<string>} The shared chat URL
 */
export const shareChat = async (id: string) => {
  try {
    startLoading();
    const res = await services.share.shareChat(id);
    if (res.content && res.content.token) {
      const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : Config.WEBSITE_DOMAIN}/shared/chats/${res.content.token}`;
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        Clipboard.setString(shareUrl);
      }
      showGlobalToast({
        message: tr('chat.shareLinkCopied'),
        type: ToastType.HELP,
      });
    } else {
      showGlobalToast({
        message: tr('chat.shareError'),
        type: ToastType.ERROR,
      });
    }
  } catch (ex) {
    // Share failed
  } finally {
    finishLoading();
  }
};

/**
 * Toggles the favorite status of a chat
 *
 * @param {number} chatId - ID of the chat to toggle favorite status
 * @param {boolean} inFavorite - New favorite status
 * @param {string} [tab_id] - Optional tab ID to associate with the favorite
 * @returns {Promise<boolean>} Whether the operation was successful
 */
export const toggleChatFavorite = async (
  chatId: number,
  inFavorite: boolean,
  tab_id?: string,
): Promise<any> => {
  try {
    startLoading();
    let res = null;
    if (inFavorite) {
      // Add to favorites - pass tab_id if provided
      res = await services.chats.addFavorite(chatId, tab_id);
    } else {
      // Remove from favorites
      res = await services.chats.removeFavorite(chatId);
    }
    return res;
  } catch (ex) {
    return false;
  } finally {
    finishLoading();
  }
};

/**
 * Deletes a chat from history
 *
 * @param {number} id - ID of the chat to delete
 * @returns {Promise<boolean>} Whether the operation was successful
 */
export const deleteChat = async (id: number) => {
  try {
    startLoading();
    await services.chats.removeChat(id);
    return true;
  } catch (ex) {
    return false;
  } finally {
    finishLoading();
  }
};

/**
 * Submits a question to the AI and processes streaming response
 * The backend returns newline-delimited JSON objects (NDJSON)
 * accumulating chunks in a buffer and splitting them on "\n".
 *
 * @param {QNAForm} body - The question data containing user query and context
 * @return {Promise<void>} Resolves when the streaming is complete
 */
export const submitQNAStreaming = async (requestPayload: QNAForm) => {
  const id = generateUniqueId('assistant');
  try {
    const body = {
      question: requestPayload.question || '',
      ...(requestPayload.thread_id && { thread_id: requestPayload.thread_id }),
      ...(requestPayload.question_id && { category_id: requestPayload.question_id }),
    };
    const { messages } = store.getState().chat || {};
    const newMessages: MessageItem[] = [
      {
        id: generateUniqueId('user'),
        content: body.question || '',
        message: body.question || '',
        role: 'user',
        created_at: new Date().toISOString(),
      },
      ...(messages || []),
    ];

    store.dispatch(setMessages({ messages: newMessages }));

    // Clear metadata and references FIRST (ensures phase 1 starts fresh)
    // This MUST happen before setting empty assistant message to avoid race condition
    store.dispatch(clearMetadata());
    store.dispatch(setReferences([]));
    store.dispatch(setShowDebug(false));
    // Clear legacy 'data' array to ensure clean state
    store.dispatch(setChat({ data: [] }));

    // Create an empty assistant message immediately to show loading indicator
    const emptyAssistantMessage: MessageItem[] = [
      {
        id,
        content: '',
        message: '',
        role: 'assistant',
        created_at: new Date().toISOString(),
        references: [],
      },
      ...newMessages,
    ];

    store.dispatch(setMessages({ messages: emptyAssistantMessage }));
    store.dispatch(setLoadingMessages());
    // Set streaming message ID to enable animation
    store.dispatch(setStreamMessageLoadingId(id));

    // Abort any previous stream
    if (typeof window !== 'undefined' && window.streamController) {
      window.streamController.abort();
    }
    if (typeof window !== 'undefined') {
      window.streamController = new AbortController();
    }

    // Build final URL - ensure no double slashes
    const cleanBaseUrl = baseUrl.endsWith('/')
      ? baseUrl.slice(0, -1)
      : baseUrl;
    const cleanApiPath = api.qna.ask.startsWith('/')
      ? api.qna.ask
      : '/' + api.qna.ask;
    const finalUrl = `${cleanBaseUrl}${cleanApiPath}`;

    const response = await fetch(finalUrl, {
      signal:
        typeof window !== 'undefined'
          ? window.streamController?.signal
          : undefined,
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${store.getState().auth?.currentUser?.token}`,
      },
      body: JSON.stringify(body),
    });
    if (response.status === 401) {
      await endSession();
      throw new Error('Unauthorized access. Please login again.');
    }
    // Handle all HTTP errors (404, 500, etc.)
    if (!response.ok) {
      throw new Error(
        `Server error (${response.status}): ${response.statusText || 'Please try again later.'}`,
      );
    }
    if (!response.body) {
      throw new Error('ReadableStream not supported');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let tempMessage = '';
    const metadata: any = {};
    const linkMetadata: LinksMetaData = {};
    const requestedMetadataUrls = new Set<string>();

    /**
     * Throttled version of dispatch to prevent too many UI updates
     * Throttle delay set to 100ms to balance responsiveness and performance
     */
    const throttledDispatch = throttle(updatedMessages => {
      store.dispatch(setMessages({ messages: updatedMessages }));
    }, 100);

    /**
     * Helper function to update the streaming message with current content and metadata
     */
    const updateStreamingMessage = () => {
      const { messages } = store.getState().chat || {};
      const tempMessages = messages?.filter(m => m.id !== id) || [];

      // Create the message object
      const messageObject = {
        id,
        content: tempMessage,
        message: tempMessage,
        role: 'assistant',
        created_at: new Date().toISOString(),
        references: metadata.references || [],
        ...(Object.keys(metadata).length > 0 ? metadata : {}),
      };

      const updatedMessages = [messageObject, ...tempMessages];
      throttledDispatch(updatedMessages);
    };

    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        // Decode any remaining buffer when stream is done
        if (buffer) {
          buffer += decoder.decode();
          const lines = buffer.split('\n');
          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              const parsedObj = JSON.parse(line);
              if (parsedObj.text) {
                tempMessage += parsedObj.text;
              }
            } catch (e) {
              // Ignore parse errors for final chunk
            }
          }
        }
        break;
      }

      // Decode the current chunk (use stream: true for all chunks except the last)
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      // Keep the last partial line in the buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim()) continue;

        try {
          const parsedObj = JSON.parse(line);

          if (parsedObj.error) throw new Error(parsedObj.error);

          // Process text chunks
          if (parsedObj.text) {
            tempMessage += parsedObj.text;
            updateStreamingMessage();
          }
          // Append references to metadata.references if present
          if (parsedObj.references) {
            // If metadata.references already exists, append; otherwise, initialize it.
            metadata.references = metadata.references
              ? [...metadata.references, ...parsedObj.references]
              : [...parsedObj.references];
            // update store references for UI to consume immediately
            try {
              store.dispatch(setReferences(metadata.references));
            } catch (e) {
              // setReferences dispatch failed
            }

            // IMMEDIATELY update the message with the new references
            updateStreamingMessage();

            // Prefetch metadata for new references (non-blocking)
            const urls = (metadata.references || [])
              .map((ref: any) => ref?.full_link || ref?.genio_url || '')
              .filter((url: string) => !!url);
            void Promise.all(
              urls.map(async (url: string) => {
                if (requestedMetadataUrls.has(url)) {
                  return;
                }
                requestedMetadataUrls.add(url);
                try {
                  const meta = await parseMetadata(url);
                  linkMetadata[url] = meta;
                  store.dispatch(setMetadata({ [url]: meta }));
                } catch (e) {
                  // metadata prefetch failed
                }
              }),
            );
          }

          if (!parsedObj.references && parsedObj.citations) {
            const citations = Array.isArray(parsedObj.citations)
              ? parsedObj.citations
              : [];
            metadata.references = citations;
            store.dispatch(setReferences(citations));
            updateStreamingMessage();
          }

          if (
            parsedObj.links_meta ||
            parsedObj.linksMeta ||
            parsedObj.links_metadata ||
            parsedObj.metadata
          ) {
            const chunkMeta =
              parsedObj.links_meta ||
              parsedObj.linksMeta ||
              parsedObj.links_metadata ||
              parsedObj.metadata;
            Object.assign(linkMetadata, chunkMeta);
            store.dispatch(setMetadata(chunkMeta));
            if (!metadata.references) {
              const fallbackRefs = Object.keys(chunkMeta || {});
              if (fallbackRefs.length > 0) {
                metadata.references = fallbackRefs;
                store.dispatch(setReferences(fallbackRefs));
                updateStreamingMessage();
              }
            }
          }

          if (parsedObj.dev_message_id && !metadata.dev_message_id) {
            metadata.dev_message_id = parsedObj.dev_message_id;
            store.dispatch(setShowDebug(true));
            // Flush throttle and dispatch immediately for phase 2 transition
            throttledDispatch.flush();
            updateStreamingMessage();
            throttledDispatch.flush();
          }

          // Handle thread metadata (old API returns thread_id)
          if (parsedObj.thread_id !== undefined) {
            const threadId = parsedObj.thread_id.toString();
            metadata.thread_id = threadId;
            metadata.conversation_title = parsedObj.title || parsedObj.conversation_title;
            updateStreamingMessage();
            if (!requestPayload.thread_id) {
              store.dispatch(
                createNewChat({
                  id: threadId,
                  model_id: threadId,
                  title: parsedObj.title || parsedObj.conversation_title || 'Untitled',
                  created_at: new Date().toISOString(),
                  is_favorite: false,
                  layout:
                    store.getState().chat?.selectedChatLayoutDirection || 'ltr',
                  is_local: true,
                }),
              );
              // ensure selected chat is set for subsequent actions (share/favorite)
              // Call selectChat after createNewChat to ensure chat is found in array
              try {
                store.dispatch(selectChat(threadId));
              } catch (e) {
                // ignore
              }
              if (typeof window !== 'undefined') {
                // Build URL with category params to preserve checkbox state
                const baseUrl = `chat/${encryptUrlParam(threadId)}/${encryptUrlParam(store.getState().chat?.selectedChatLayoutDirection)}`;
                const params = new URLSearchParams();

                // Add category information if available
                if (requestPayload.question_id) {
                  params.append('categoryId', requestPayload.question_id.toString());
                }
                if (requestPayload.category_key) {
                  params.append('categoryKey', requestPayload.category_key);
                }
                if (requestPayload.tab_id) {
                  params.append('tab_id', requestPayload.tab_id.toString());
                }

                const finalUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

                window.history.pushState(
                  {},
                  '',
                  finalUrl,
                );
              }
              let categories = await getLocalData(keys.CHAT_CATEGORIES);
              if (!categories) categories = {};
              categories[threadId] = requestPayload.question_id;
              await setLocalData(keys.CHAT_CATEGORIES, categories);
            }
          }
        } catch (parseError) {
          // Continue processing other lines even if one fails
        }
      }
    }

    throttledDispatch.flush();
  } catch (error: any) {
    if (!error.message?.includes('BodyStreamBuffer was aborted')) {
      store.dispatch(setErrorMessages());
      showGlobalToast({
        message: tr('app.error'),
        type: ToastType.ERROR,
      });
    }
    // Remove the empty streaming message on error (toast already shows the error)
    if (
      typeof window !== 'undefined' &&
      !window.streamController?.signal?.aborted
    ) {
      const { messages } = store.getState().chat || {};
      const tempMessages = messages?.filter(m => m.id !== id) || [];
      store.dispatch(setMessages({ messages: tempMessages }));
    }
  } finally {
    console.warn('finally');
    store.dispatch(setLoadingMessagesEnd());
    if (typeof window !== 'undefined' && window.streamController) {
      window.streamController.abort();
    }
  }
};

// Legacy React Native functions (keeping for backward compatibility)

export const getLastMessageByUser = (
  messages: MessageItem[],
): MessageItem | null => {
  const { id: userId } = store.getState()?.auth?.currentUser || {};
  if (!userId || !messages || messages.length === 0) {
    return null;
  }
  const userMessages = messages
    .filter(message => message.sender_id + '' === userId + '')
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

  return userMessages.length > 0 ? userMessages[0] : null;
};

export const sendMessage = async ({
  question,
  question_id,
  tryAgain = false,
  scrollToEnd,
  type,
  tab_id,
}: SendMessageWithImageParams): Promise<boolean> => {
  try {
    const {
      id: userId,
      first_name,
      last_name,
      specialty_id,
    } = store.getState()?.auth?.currentUser || {};
    const { threadId, data, chats } = store.getState().chat;
    let tempData = [...(data || [])];

    store.dispatch(setMessageIsLoading());
    // Clear 'messages' array to ensure Chat screen uses 'data' (legacy flow)
    // This prevents stale messages from submitQNAStreaming from being displayed
    store.dispatch(setMessages({ messages: [] }));
    if (!question) {
      throw new Error('Question text must be provided');
    }

    const newMessage: MessageItem = {
      id: new Date().toISOString(),
      role: 'user',
      message: question || '',
      sender_id: userId + '',
      sender_name: first_name + ' ' + last_name,
      thread_id: threadId + '',
      status: MessageStatus.ReadByEveryone,
      created_at: new Date().toISOString(),
    };

    if (!tryAgain) {
      tempData = [newMessage, ...tempData];
      store.dispatch(setChat({ data: tempData }));
    }
    scrollToEnd?.();
    store.dispatch(
      setTypingStatus({ status: { userId: 'genio', isTyping: true } }),
    );

    const body: QNAForm = {
      question: question || '',
      question_id: question_id,
      specialty_id: specialty_id || -1,
      type,
      user_id: userId || -1,
      thread_id: threadId,
      ...(tab_id && {
        tab_id: typeof tab_id === 'string' ? parseInt(tab_id, 10) : tab_id,
      }),
    };

    const messageId = dayjs().add(1, 'minute').toISOString();
    store.dispatch(setStreamMessageLoadingId(messageId));

    // Clear metadata and references FIRST (ensures phase 1 starts fresh)
    // This MUST happen before setChat to avoid race condition with component re-render
    store.dispatch(clearMetadata());
    store.dispatch(setReferences([]));

    // Create an empty assistant message immediately to show loading indicator
    const emptyAssistantMessage: MessageItem = {
      message: '',
      role: 'assistant' as const,
      sender_id: 'genio',
      sender_name: 'GENIO',
      thread_id: threadId + '',
      status: MessageStatus.ReadByEveryone,
      created_at: new Date().toISOString(),
      citations: [],
      references: [],
      id: messageId,
    };
    store.dispatch(setChat({ data: [emptyAssistantMessage, ...tempData] }));

    let citationsList: string[] = [];
    let devMessageId: number | undefined = undefined;

    // Throttle chat store updates
    const throttledDispatch = throttle(
      (updatedMessages: string, refs?: any[]) => {
        const messageData: MessageItem = {
          message: cleanAndFormatMarkdown(updatedMessages || ''),
          role: 'assistant' as const,
          sender_id: 'genio',
          sender_name: 'GENIO',
          thread_id: threadId + '',
          status: MessageStatus.ReadByEveryone,
          created_at: new Date().toISOString(),
          citations: refs || citationsList,
          references: refs || citationsList,
          id: messageId,
          ...(devMessageId ? { dev_message_id: devMessageId } : {}),
        };

        store.dispatch(
          setChat({
            data: [messageData, ...tempData],
          }),
        );
      },
      100,
      {
        leading: true,
        trailing: true,
      },
    );

    let prevText = '';
    let accumulatedText = '';
    let num = 0;

    const res = await services.qna.sendStreamingQNA(
      body,
      newThreadId => {
        if (!threadId && newThreadId) {
          store.dispatch(setCurrentThreadId(newThreadId));

          store.dispatch(
            setChats([
              {
                id: newThreadId,
                title: question
                  ? question.length > 40
                    ? question.substring(0, 40) + '...'
                    : question
                  : '',
                created_at: new Date().toISOString(),
                is_favorite: false,
              },
              ...chats,
            ]),
          );
        }
      },
      async (accumulatedAnswer, citations) => {
        // Update citations/references immediately when they arrive
        if (Array.isArray(citations) && citations?.length > 0) {
          citationsList = citations || [];
          // Dispatch references to Redux store for immediate UI update
          store.dispatch(setReferences(citationsList));

          // IMMEDIATELY update the message with references even if no text yet
          const messageData: MessageItem = {
            message: cleanAndFormatMarkdown(accumulatedAnswer || ''),
            role: 'assistant' as const,
            sender_id: 'genio',
            sender_name: 'GENIO',
            thread_id: threadId + '',
            status: MessageStatus.ReadByEveryone,
            created_at: new Date().toISOString(),
            citations: citationsList,
            references: citationsList,
            id: messageId,
            ...(devMessageId ? { dev_message_id: devMessageId } : {}),
          };

          store.dispatch(
            setChat({
              data: [messageData, ...tempData],
            }),
          );
        }

        if (prevText.length - accumulatedText.length > 40) {
          accumulatedText = accumulatedAnswer;
          num += 1;

          const messageData: MessageItem = {
            message: cleanAndFormatMarkdown(accumulatedAnswer || ''),
            role: 'assistant' as const,
            sender_id: 'genio',
            sender_name: 'GENIO',
            thread_id: threadId + '',
            status: MessageStatus.ReadByEveryone,
            created_at: new Date().toISOString(),
            citations: citationsList,
            references: citationsList,
            id: messageId,
            ...(devMessageId ? { dev_message_id: devMessageId } : {}),
          };

          store.dispatch(
            setChat({
              data: [messageData, ...tempData],
            }),
          );

          if (num === 1) {
            store.dispatch(
              setTypingStatus({ status: { userId: 'genio', isTyping: false } }),
            );
          }
        }
        prevText = accumulatedAnswer;
      },
    );
    const finalMessageData: MessageItem = {
      message: cleanAndFormatMarkdown(prevText || ''),
      role: 'assistant' as const,
      sender_id: 'genio',
      sender_name: 'GENIO',
      thread_id: threadId + '',
      status: MessageStatus.ReadByEveryone,
      created_at: new Date().toISOString(),
      citations: citationsList,
      references: citationsList,
      id: messageId,
      ...(devMessageId ? { dev_message_id: devMessageId } : {}),
    };

    store.dispatch(
      setChat({
        data: [finalMessageData, ...tempData],
      }),
    );

    // Flush any remaining updates after the loop
    throttledDispatch.flush();

    if (!res) {
      store.dispatch(setMessageIsFail());
      throw new Error('Error inserting message: ');
    }
    store.dispatch(setMessageSuccess());
    return true;
  } catch (error) {
    return false;
  } finally {
    store.dispatch(
      setTypingStatus({ status: { userId: 'genio', isTyping: false } }),
    );
    finishLoading();
  }
};

export const onStreamingMessageEndAnimation = () => {
  const { streamMessageLoadingId } = store.getState().chat;
  if (streamMessageLoadingId) {
    store.dispatch(setStreamMessageLoadingId(null));
  }
};

export const resetChat = () => {
  store.dispatch(setChat({ data: [] }));
  store.dispatch(setCurrentThreadId(undefined));
  store.dispatch(setMessageSuccess());
  // Clear new chat state as well
  try {
    store.dispatch(setMessages({ messages: [] }));
  } catch { }
  try {
    store.dispatch(setReferences([]));
  } catch { }
  try {
    store.dispatch(clearMetadata());
  } catch { }
  try {
    store.dispatch(selectChat(''));
  } catch { }
};
